import * as React from 'react';
import { useEffect, useState } from "react";
import { useErrorStore } from "../../store/useErrorStore";
import { useAuditLogsStore } from "../../store/useAuditLogsStore";
import { Typography, Table, Sheet } from "@mui/joy";
import Box from '@mui/joy/Box';
import Chip from '@mui/joy/Chip';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import Link from '@mui/joy/Link';
import Input from '@mui/joy/Input';
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';
import ModalClose from '@mui/joy/ModalClose';
import Select from '@mui/joy/Select';
import Option from '@mui/joy/Option';
import Checkbox from '@mui/joy/Checkbox';
import IconButton, { iconButtonClasses } from '@mui/joy/IconButton';
import LinearProgress from '@mui/joy/LinearProgress';
import SearchIcon from '@mui/icons-material/Search';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';


function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
   if (orderBy === 'date') {
      const dateA = new Date(a[orderBy] as string);
      const dateB = new Date(b[orderBy] as string);
      return dateB.getTime() - dateA.getTime();
   }
   if (b[orderBy] < a[orderBy]) return -1;
   if (b[orderBy] > a[orderBy]) return 1;
   return 0;
}

type auditLogOrder = 'asc' | 'desc';

function getComparator<Key extends keyof AuditLog>(
   order: 'asc' | 'desc',
   orderBy: Key
): (a: AuditLog, b: AuditLog) => number {
   return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
}

interface AuditLog {
   id: number;
   action: string;
   table_name: string;
   row_id: string;
   changed_by: string;
   changed_at: string;
   changed_data: string;
}

export default function AuditLogsTable() {
   const [order, setOrder] = React.useState<auditLogOrder>('desc');
   const [selected, setSelected] = React.useState<readonly string[]>([]);
   const [open, setOpen] = React.useState(false);
   const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
   const { error, setError } = useErrorStore();
   const {
      auditLogs: storeLogs,
      loading: storeLoading,
      refresh,
   } = useAuditLogsStore();
   const [dataLoading, setDataLoading] = useState(false);
   const [searchTerm, setSearchTerm] = useState("");
   const [originalAuditLogs, setOriginalAuditLogs] = useState<AuditLog[]>([]);
   const [auditLogOrder, setAuditLogOrder] = useState<'asc' | 'desc'>('desc');
   const [auditLogOrderBy, setAuditLogOrderBy] = useState<keyof AuditLog>('id');
   const [openLog, setOpenLog] = useState<AuditLog | null>(null);

   const [page, setPage] = useState(0);
   const [rowsPerPage, setRowsPerPage] = useState(50);
   const handleChangePage = (newPage: number) => {
      setPage(newPage);
   };
   const handleChangeRowsPerPage = (_: any, newValue: number | null) => {
      setRowsPerPage(parseInt(newValue!.toString(), 10));
      setPage(0);
   };
   const getLabelDisplayedRowsTo = () => {
      if (filteredAuditLogs.length === -1) {
         return (page + 1) * rowsPerPage;
      }
      return rowsPerPage === -1
         ? filteredAuditLogs.length
         : Math.min(filteredAuditLogs.length, (page + 1) * rowsPerPage);
   };

   useEffect(() => {
      refresh();
   }, [refresh]);

   useEffect(() => {
      setAuditLogs(storeLogs);
      setOriginalAuditLogs(storeLogs);
   }, [storeLogs]);

   useEffect(() => {
      setDataLoading(storeLoading);
   }, [storeLoading]);

   if (dataLoading && auditLogs.length === 0) {
      return <Typography>Loading audit logs...</Typography>;
   }

   if (error) {
      return <Typography color="danger">{error}</Typography>;
   }

   // filteredAuditLogs now just uses auditLogs, which is set by searchAuditLogs
   const filteredAuditLogs = auditLogs;

   const searchAuditLogs = async (query: string) => {
      setSearchTerm(query);
      if (!query) {
         setAuditLogs(originalAuditLogs);
         return;
      }
      const lower = query.toLowerCase();
      const filtered = originalAuditLogs.filter((log) =>
         log.action.toLowerCase().includes(lower) ||
         log.table_name.toLowerCase().includes(lower) ||
         log.row_id.toLowerCase().includes(lower) ||
         log.changed_by.toLowerCase().includes(lower) ||
         log.changed_data.toLowerCase().includes(lower)
      );
      setAuditLogs(filtered);
   };

   return (
      <React.Fragment>
         <Sheet
            className="SearchAndFilters-mobile"
            sx={{ display: { xs: 'flex', sm: 'none' }, my: 1, gap: 1 }}
         >
            <Input
               size="sm"
               placeholder="Search"
               startDecorator={<SearchIcon />}
               sx={{ flexGrow: 1 }}
               value={searchTerm}
               onChange={(e) => {
                  searchAuditLogs(e.target.value);
               }}
            />
         </Sheet>
         <Box
            className="SearchAndFilters-tabletUp"
            sx={{
               borderRadius: 'sm',
               py: 2,
               display: { xs: 'flex' },
               flexWrap: 'wrap',
               gap: 1.5,
               '& > *': {
                  minWidth: { xs: '120px', md: '160px' },
               },
            }}
         >
            <FormControl sx={{ flex: 1 }} size="sm">
               <FormLabel>Search for audit log</FormLabel>
               <Input
                  size="sm"
                  placeholder="Search"
                  startDecorator={<SearchIcon />}
                  value={searchTerm}
                  onChange={(e) => {
                     searchAuditLogs(e.target.value);
                  }}
               />
            </FormControl>
         </Box>
         <Sheet
            className="OrderTableContainer"
            variant="outlined"
            sx={{
               display: { xs: 'none', sm: 'initial' },
               width: '100%',
               borderRadius: 'sm',
               flexShrink: 1
            }}
         >
            {dataLoading && (
               <Box sx={{ width: '100%' }}>
                  <LinearProgress />
               </Box>
            )}
            <Box
               sx={{
                  width: '100%',
                  flexShrink: 1,
                  overflow: 'auto',
                  maxHeight: '75vh',
               }}
            >
               <Table
                  aria-labelledby="tableTitle"
                  stickyHeader
                  hoverRow
                  sx={{
                     '--TableCell-headBackground': 'var(--joy-palette-background-level1)',
                     '--Table-headerUnderlineThickness': '1px',
                     '--TableRow-hoverBackground': 'var(--joy-palette-background-level1)',
                     '--TableCell-paddingY': '4px',
                     '--TableCell-paddingX': '8px',
                  }}
               >
                  <thead>
                     <tr>
                        <th style={{ width: 48, textAlign: 'center', padding: '12px 6px' }}>
                           <Checkbox
                              size="sm"
                              indeterminate={
                                 selected.length > 0 && selected.length !== (auditLogs?.length || 0)
                              }
                              checked={selected.length === (auditLogs?.length || 0)}
                              onChange={(event) => {
                                 setSelected(
                                    event.target.checked ? (auditLogs || []).map((log) => log.id.toString()) : [],
                                 );
                              }}
                              color={
                                 selected.length > 0 || selected.length === (auditLogs?.length || 0)
                                    ? 'primary'
                                    : undefined
                              }
                              sx={{ verticalAlign: 'text-bottom' }}
                           />
                        </th>
                        <th style={{ width: 50, padding: '12px 6px' }}>
                           <Link
                              underline="none"
                              color="primary"
                              component="button"
                              onClick={() => {
                                 const isAsc = auditLogOrderBy === 'id' && auditLogOrder === 'asc';
                                 setAuditLogOrder(isAsc ? 'desc' : 'asc');
                                 setAuditLogOrderBy('id');
                              }}
                              endDecorator={<ArrowDropDownIcon />}
                              sx={[
                                 {
                                    fontWeight: 'lg',
                                    '& svg': {
                                       transition: '0.2s',
                                       transform:
                                          auditLogOrderBy === 'id' && auditLogOrder === 'desc'
                                             ? 'rotate(0deg)'
                                             : 'rotate(180deg)',
                                    },
                                 },
                                 auditLogOrderBy === 'id' && auditLogOrder === 'desc'
                                    ? { '& svg': { transform: 'rotate(0deg)' } }
                                    : { '& svg': { transform: 'rotate(180deg)' } },
                              ]}
                           >
                              ID
                           </Link>
                        </th>
                        <th style={{ width: 80, padding: '12px 6px' }}>
                           <Link
                              underline="none"
                              color="primary"
                              component="button"
                              onClick={() => {
                                 const isAsc = auditLogOrderBy === 'action' && auditLogOrder === 'asc';
                                 setAuditLogOrder(isAsc ? 'desc' : 'asc');
                                 setAuditLogOrderBy('action');
                              }}
                              endDecorator={<ArrowDropDownIcon />}
                              sx={[
                                 { fontWeight: 'lg', '& svg': { transition: '0.2s' } },
                                 auditLogOrderBy === 'action' && auditLogOrder === 'desc'
                                    ? { '& svg': { transform: 'rotate(0deg)' } }
                                    : { '& svg': { transform: 'rotate(180deg)' } },
                              ]}
                           >
                              Action
                           </Link>
                        </th>
                        <th style={{ width: 100, padding: '12px 6px' }}>
                           <Link
                              underline="none"
                              color="primary"
                              component="button"
                              onClick={() => {
                                 const isAsc = auditLogOrderBy === 'table_name' && auditLogOrder === 'asc';
                                 setAuditLogOrder(isAsc ? 'desc' : 'asc');
                                 setAuditLogOrderBy('table_name');
                              }}
                              endDecorator={<ArrowDropDownIcon />}
                              sx={[
                                 { fontWeight: 'lg', '& svg': { transition: '0.2s' } },
                                 auditLogOrderBy === 'table_name' && auditLogOrder === 'desc'
                                    ? { '& svg': { transform: 'rotate(0deg)' } }
                                    : { '& svg': { transform: 'rotate(180deg)' } },
                              ]}
                           >
                              Entity
                           </Link>
                        </th>
                        <th style={{ width: 100, padding: '12px 6px' }}>
                           <Link
                              underline="none"
                              color="primary"
                              component="button"
                              onClick={() => {
                                 const isAsc = auditLogOrderBy === 'row_id' && auditLogOrder === 'asc';
                                 setAuditLogOrder(isAsc ? 'desc' : 'asc');
                                 setAuditLogOrderBy('row_id');
                              }}
                              endDecorator={<ArrowDropDownIcon />}
                              sx={[
                                 { fontWeight: 'lg', '& svg': { transition: '0.2s' } },
                                 auditLogOrderBy === 'row_id' && auditLogOrder === 'desc'
                                    ? { '& svg': { transform: 'rotate(0deg)' } }
                                    : { '& svg': { transform: 'rotate(180deg)' } },
                              ]}
                           >
                              Entity ID
                           </Link>
                        </th>
                        <th style={{ width: 150, padding: '12px 6px' }}>
                           <Link
                              underline="none"
                              color="primary"
                              component="button"
                              onClick={() => {
                                 const isAsc = auditLogOrderBy === 'changed_by' && auditLogOrder === 'asc';
                                 setAuditLogOrder(isAsc ? 'desc' : 'asc');
                                 setAuditLogOrderBy('changed_by');
                              }}
                              endDecorator={<ArrowDropDownIcon />}
                              sx={[
                                 { fontWeight: 'lg', '& svg': { transition: '0.2s' } },
                                 auditLogOrderBy === 'changed_by' && auditLogOrder === 'desc'
                                    ? { '& svg': { transform: 'rotate(0deg)' } }
                                    : { '& svg': { transform: 'rotate(180deg)' } },
                              ]}
                           >
                              User
                           </Link>
                        </th>
                        <th style={{ width: 170, padding: '12px 6px' }}>
                           <Link
                              underline="none"
                              color="primary"
                              component="button"
                              onClick={() => {
                                 const isAsc = auditLogOrderBy === 'changed_at' && auditLogOrder === 'asc';
                                 setAuditLogOrder(isAsc ? 'desc' : 'asc');
                                 setAuditLogOrderBy('changed_at');
                              }}
                              endDecorator={<ArrowDropDownIcon />}
                              sx={[
                                 { fontWeight: 'lg', '& svg': { transition: '0.2s' } },
                                 auditLogOrderBy === 'changed_at' && auditLogOrder === 'desc'
                                    ? { '& svg': { transform: 'rotate(0deg)' } }
                                    : { '& svg': { transform: 'rotate(180deg)' } },
                              ]}
                           >
                              Timestamp
                           </Link>
                        </th>
                        <th style={{ width: 150, padding: '12px 6px' }}>
                           <Link
                              underline="none"
                              color="primary"
                              component="button"
                              onClick={() => {
                                 const isAsc = auditLogOrderBy === 'changed_data' && auditLogOrder === 'asc';
                                 setAuditLogOrder(isAsc ? 'desc' : 'asc');
                                 setAuditLogOrderBy('changed_data');
                              }}
                              endDecorator={<ArrowDropDownIcon />}
                              sx={[
                                 { fontWeight: 'lg', '& svg': { transition: '0.2s' } },
                                 auditLogOrderBy === 'changed_data' && auditLogOrder === 'desc'
                                    ? { '& svg': { transform: 'rotate(0deg)' } }
                                    : { '& svg': { transform: 'rotate(180deg)' } },
                              ]}
                           >
                              Changed Data
                           </Link>
                        </th>
                     </tr>
                  </thead>
                  <tbody>
                     {filteredAuditLogs.length > 0 ? (
                        [...filteredAuditLogs]
                           .sort(getComparator(auditLogOrder, auditLogOrderBy))
                           .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                           .map((log) => (
                              <tr key={log.id}>
                                 <td style={{ textAlign: 'center', width: 120 }}>
                                    <Checkbox
                                       size="sm"
                                       checked={selected.includes(log.id.toString())}
                                       color={selected.includes(log.id.toString()) ? 'primary' : undefined}
                                       onChange={(event) => {
                                          setSelected((ids) =>
                                             event.target.checked ? ids.concat(log.id.toString()) : ids.filter((itemId) => itemId !== log.id.toString()),
                                          );
                                       }}
                                       slotProps={{ checkbox: { sx: { textAlign: 'left' } } }}
                                       sx={{ verticalAlign: 'text-bottom' }}
                                    />
                                 </td>
                                 <td>
                                    <Typography level="body-xs">{log.id}</Typography>
                                 </td>
                                 <td>
                                    <Typography level="body-xs">
                                       {(() => {
                                          try {
                                             const parsed = JSON.parse(log.changed_data ?? '{}');
                                             if (parsed.deleted_at) return 'DELETED';
                                             return log.action === 'INSERT' ? 'ADDED' : log.action ?? '-';
                                          } catch {
                                             return log.action === 'INSERT' ? 'ADDED' : log.action ?? '-';
                                          }
                                       })()}
                                    </Typography>
                                 </td>
                                 <td>
                                    <Typography level="body-xs">{log.table_name ?? '-'}</Typography>
                                 </td>
                                 <td>
                                    <Typography level="body-xs">{log.row_id ?? '-'}</Typography>
                                 </td>
                                 <td>
                                    <Typography level="body-xs">{log.changed_by ?? '-'}</Typography>
                                 </td>
                                 <td>
                                    <Typography level="body-xs">
                                      {(() => {
                                        try {
                                          const date = new Date(log.changed_at);
                                          const istDate = new Intl.DateTimeFormat('en-IN', {
                                            timeZone: 'Asia/Kolkata',
                                            year: 'numeric',
                                            month: 'short',
                                            day: '2-digit',
                                            hour: '2-digit',
                                            minute: '2-digit',
                                            second: '2-digit',
                                            hour12: false,
                                          }).format(date);
                                          return istDate;
                                        } catch {
                                          return log.changed_at ?? '-';
                                        }
                                      })()}
                                    </Typography>
                                 </td>
                                 <td>
                                    {(() => {
                                       try {
                                          const parsed = JSON.parse(log.changed_data ?? '{}');
                                          if (parsed.deleted_at) {
                                             return <Chip color="danger" variant="outlined" size="sm">Deleted</Chip>;
                                          }
                                          return <Box sx={{ display: 'flex', gap: 2, px: 1, alignItems: 'center' }}>
                                             <Link level="body-xs" component="a" onClick={() => setOpenLog(log)}>View</Link>
                                          </Box>;
                                       } catch {
                                          return <Box sx={{ display: 'flex', gap: 2, px: 1, alignItems: 'center' }}>
                                             <Link level="body-xs" component="a" onClick={() => setOpenLog(log)}>View</Link>
                                          </Box>;
                                       }
                                    })()}


                                 </td>
                              </tr>
                           ))
                     ) : (
                        <tr>
                           <td colSpan={8} style={{ textAlign: "center" }}>No audit logs found</td>
                        </tr>
                     )}
                  </tbody>
               </Table>
            </Box>
            <Table>
               <tfoot>
                  <tr>
                     <td colSpan={9} style={{ position: 'sticky', bottom: 0, background: '#fff', zIndex: 1, backgroundColor: 'var(--joy-palette-background-level1)' }}>
                        <Box
                           sx={{
                              maxWidth: '100%',
                              overflowX: 'auto',
                           }}
                        >
                           <Box
                              sx={{
                                 display: 'flex',
                                 alignItems: 'center',
                                 gap: 2,
                                 justifyContent: 'flex-end',
                                 px: 2,
                                 py: 1,
                                 minWidth: '600px',
                              }}
                           >
                              <FormControl orientation="horizontal" size="sm">
                                 <FormLabel>Rows per page:</FormLabel>
                                 <Select onChange={handleChangeRowsPerPage} value={rowsPerPage}>
                                    <Option value={25}>25</Option>
                                    <Option value={50}>50</Option>
                                    <Option value={100}>100</Option>
                                 </Select>
                              </FormControl>
                              <Typography sx={{ textAlign: 'center', minWidth: 80 }}>
                                 {`${filteredAuditLogs.length === 0 ? 0 : page * rowsPerPage + 1}–${getLabelDisplayedRowsTo()} of ${filteredAuditLogs.length}`}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                 <IconButton
                                    size="sm"
                                    color="neutral"
                                    variant="outlined"
                                    disabled={page === 0}
                                    onClick={() => handleChangePage(page - 1)}
                                 >
                                    <KeyboardArrowLeftIcon />
                                 </IconButton>
                                 <IconButton
                                    size="sm"
                                    color="neutral"
                                    variant="outlined"
                                    disabled={
                                       filteredAuditLogs.length !== -1
                                          ? page >= Math.ceil(filteredAuditLogs.length / rowsPerPage) - 1
                                          : false
                                    }
                                    onClick={() => handleChangePage(page + 1)}
                                 >
                                    <KeyboardArrowRightIcon />
                                 </IconButton>
                              </Box>
                           </Box>
                        </Box>
                     </td>
                  </tr>
               </tfoot>
            </Table>
         </Sheet>
         <Modal open={!!openLog} onClose={() => setOpenLog(null)}>
            <ModalDialog>
               <ModalClose />
               <Typography level="h4">Changed Data</Typography>
               <Box sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
                  {(() => {
                     try {
                        const parsed = JSON.parse(openLog?.changed_data ?? '{}');
                        return Object.entries(parsed)
                           .filter(([key]) => !['created_at', 'created_by', 'deleted_at', 'updated_at', 'updated_by'].includes(key))
                           .map(([key, value]) => (
                              <Typography key={key} level="body-sm">
                                 <strong>{key}:</strong> {String(value)}
                              </Typography>
                           ));
                     } catch {
                        return <Typography level="body-sm">{openLog?.changed_data ?? '-'}</Typography>;
                     }
                  })()}
               </Box>
            </ModalDialog>
         </Modal>
         <Box
            sx={{ py: 1 }}
         />
      </React.Fragment>
   );
}

